import { CognitoAuthService } from "../services/cognitoService";
import { debug } from "../utils/debugLogger";
import { getStoredIdToken, setStoredIdToken } from "./tokenStore";

const NEAR_EXPIRY_BUFFER_SECONDS = 120;

/**
 * The stored token, if it is the same user's and was issued after `token`.
 * Callers pass the token they captured from React state, which only changes on
 * login; after a forced refresh (refreshBrandAccess) the store holds a newer one
 * that lists Brands the old one does not, and it must win.
 */
function newerStoredToken(token: string): string | null {
  const stored = getStoredIdToken();
  if (!stored || stored === token) return null;
  try {
    const a = CognitoAuthService.parseTokenPayload(token);
    const b = CognitoAuthService.parseTokenPayload(stored);
    if (a?.sub && a.sub === b?.sub && typeof b?.iat === "number" && b.iat > (a.iat ?? 0)) {
      return stored;
    }
  } catch {
    /* unparseable: keep the caller's token */
  }
  return null;
}

/**
 * Return a usable Cognito ID token, refreshing through Cognito if the supplied
 * token is within 2 minutes of expiry. Falls back to the supplied token on
 * transient refresh errors so long-running flows (UGC video polling) don't fail
 * because of a momentary network blip.
 *
 * When no token is supplied it reads the module token store. Any token obtained
 * by refreshing is written back to the store so subsequent callers (axios
 * interceptor, authedFetch) reuse it instead of refreshing again.
 */
export async function getFreshIdToken(currentToken?: string): Promise<string> {
  const token = currentToken ?? getStoredIdToken();
  if (!token) return token || "";

  let expSeconds = 0;
  try {
    const payload = CognitoAuthService.parseTokenPayload(token);
    expSeconds = typeof payload?.exp === "number" ? payload.exp : 0;
  } catch {
    return token;
  }

  const nowSeconds = Math.floor(Date.now() / 1000);
  const isNearExpiry = expSeconds - nowSeconds <= NEAR_EXPIRY_BUFFER_SECONDS;
  if (!isNearExpiry) return newerStoredToken(token) ?? token;

  try {
    const session = await CognitoAuthService.getCurrentSession();
    if (session?.idToken) {
      setStoredIdToken(session.idToken);
      return session.idToken;
    }
  } catch (error) {
    debug.warn("getFreshIdToken: refresh failed, using existing token", error);
  }
  return token;
}
